import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';

export interface FilterVisibleSubTicketsParams {
    subTickets: TaskSubTicketResource[];
    currentUserId: ID;
    isArchivedShown: boolean;
    isOnlyMineShown: boolean;
    statusFilter: TaskSubTicketStatus[];

    // Assigned-designer ids; an empty list means "no filter"
    designerFilter: ID[];

    // Inclusive calendar-day bounds in 'YYYY-MM-DD', either of them optional
    dueFromDate: string | null;
    dueToDate: string | null;
}

const CALENDAR_DAY_LENGTH = 10;

// The calendar day the row actually SHOWS, not the day it falls on in UTC.
//
// The card formats `due_at` in the reader's own zone, so a phase due at 00:30 local reads as
// that day on screen. Filtering in any other frame would hide a row whose visible date is
// inside the picked range, which reads as a bug however defensible the arithmetic is.
//
// Null for a value that is not a timestamp at all. A legacy or corrupt `due_at` has no calendar
// day, so it fails closed out of an active date filter rather than being silently kept or
// rendered as "Invalid date".
export const resolveVisibleCalendarDay = (dueAt: string | null): string | null => {
    if (!dueAt) {
        return null;
    }

    const parsed = new Date(dueAt);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const year = `${parsed.getFullYear()}`.padStart(4, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsed.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
};

// Both bounds are inclusive and either may be omitted. `YYYY-MM-DD` compares correctly as a
// plain string, so there is no second date parse to disagree with the first.
export const isWithinDueDateRange = (
    dueAt: string | null,
    fromDate: string | null,
    toDate: string | null
): boolean => {
    if (fromDate === null && toDate === null) {
        // No filter: a phase with no due date is as visible as any other
        return true;
    }

    const day = resolveVisibleCalendarDay(dueAt);
    if (day === null) {
        // Asked "which phases are due in this window", a phase with no due date is not an
        // answer. It is excluded while a bound is active and comes straight back when it is not
        return false;
    }

    if (fromDate !== null && day < fromDate.slice(0, CALENDAR_DAY_LENGTH)) {
        return false;
    }

    if (toDate !== null && day > toDate.slice(0, CALENDAR_DAY_LENGTH)) {
        return false;
    }

    return true;
};

// Applied on top of the archived filter the server already ran.
//
// The archived rule is deliberately duplicated rather than trusted from the response: archiving
// a phase has to remove it from the list *immediately* while "Show archived" is off, and waiting
// for the refetch to land would leave it visible for as long as the round trip takes.
//
// Every filter narrows the one before it. None of them is a mode: turning one off restores
// exactly the rows it was hiding and nothing else.
export const filterVisibleSubTickets = ({
    subTickets,
    currentUserId,
    isArchivedShown,
    isOnlyMineShown,
    statusFilter,
    designerFilter,
    dueFromDate,
    dueToDate,
}: FilterVisibleSubTicketsParams): TaskSubTicketResource[] =>
    subTickets.filter((subTicket) => {
        if (!isArchivedShown && subTicket.archived_at !== null) {
            return false;
        }

        if (isOnlyMineShown && subTicket.assignee_user.id !== currentUserId) {
            return false;
        }

        // An empty selection means "no filter", not "match nothing"
        if (statusFilter.length > 0 && !statusFilter.includes(subTicket.status)) {
            return false;
        }

        // Ids are the selection truth: a designer who is renamed mid-session keeps their rows
        if (designerFilter.length > 0 && !designerFilter.includes(subTicket.assignee_user.id)) {
            return false;
        }

        if (!isWithinDueDateRange(subTicket.due_at, dueFromDate, dueToDate)) {
            return false;
        }

        return true;
    });
